import { API_BASE_URL } from "./client";

export interface ChatAttachmentImageRef {
  assetId: string;
  storageKey: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
}

export type HistoryTextPart = { type: "text"; text: string };
export type HistoryToolCallPart = {
  type: "tool_call";
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
};
export type HistoryPart = HistoryTextPart | HistoryToolCallPart;

export interface HistoryAttachment {
  assetId: string;
  storageKey: string;
  name: string;
  mimeType: string;
}

export interface ChatHistoryRun {
  runId: string;
  userMessage: {
    id: string;
    content: string;
    createdAt: number;
    attachments?: HistoryAttachment[];
  };
  assistantParts: HistoryPart[];
  createdAt: number;
}

export async function getChatHistory(
  projectId: string,
  token: string | null,
): Promise<ChatHistoryRun[]> {
  if (!token) return [];
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/chat/history`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) return [];
  return response.json();
}

export interface ChatTextDeltaEvent {
  type: "text_delta";
  delta: string;
}

export interface ChatToolCallEvent {
  type: "tool_call";
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
}

export interface ChatToolResultEvent {
  type: "tool_result";
  toolCallId: string;
  toolName: string;
  result: unknown;
  isError: boolean;
}

export interface ChatErrorEvent {
  type: "error";
  message: string;
}

export interface ChatDoneEvent {
  type: "done";
}

export interface ChatSystemNoticeEvent {
  type: "system_notice";
  message: string;
}

export type ChatStreamEvent =
  | ChatTextDeltaEvent
  | ChatToolCallEvent
  | ChatToolResultEvent
  | ChatSystemNoticeEvent
  | ChatErrorEvent
  | ChatDoneEvent;

/**
 * Async generator that yields parsed SSE events from the chat endpoint.
 * Handles chunked SSE data with a line-buffering approach.
 */
export async function* streamChatEvents(
  projectId: string,
  body: {
    message: string;
    attachmentImage?: ChatAttachmentImageRef | null;
    mode?: string;
  },
  token: string,
  signal: AbortSignal,
): AsyncGenerator<ChatStreamEvent> {
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/chat`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        message: body.message,
        mode: body.mode ?? "auto-detect",
        attachment_image: body.attachmentImage
          ? {
              asset_id: body.attachmentImage.assetId,
              storage_key: body.attachmentImage.storageKey,
              name: body.attachmentImage.name,
              mime_type: body.attachmentImage.mimeType,
            }
          : null,
      }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `Chat request failed: ${response.status} ${response.statusText}`,
    );
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          yield JSON.parse(raw) as ChatStreamEvent;
        } catch (e) {
          console.warn("chat: malformed SSE data, skipping:", raw, e);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Stream a background run's events — past (buffered in Redis) then live.
 * Used when ChatPanel mounts with a pendingChatRunId.
 */
export async function* streamReplayChatEvents(
  projectId: string,
  runId: string,
  token: string,
  signal: AbortSignal,
): AsyncGenerator<ChatStreamEvent> {
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/chat/${runId}/stream`,
    { headers: { Authorization: `Bearer ${token}` }, signal },
  );
  if (!response.ok) {
    throw new Error(
      `Replay stream failed: ${response.status} ${response.statusText}`,
    );
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          yield JSON.parse(raw) as ChatStreamEvent;
        } catch (e) {
          console.warn("chat: malformed SSE replay data, skipping:", raw, e);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
